import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import cn from 'classnames'
import { PhoneOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { Button, Flex } from 'antd'
import dayjs from 'dayjs'
import {
  useIsAdmin,
  useIsPractitioner,
  useFeatureGuard,
} from '../../../redux/slices/session'
import { ListError, PageHeader } from '../../Common'
import Filters from '../../Forms/Filters'
import { useOfficeAuthorizations } from '../../../redux/slices/office-authorizations'
import { toPath } from '../../../routes'
import RemindersModal from '../../Common/RemindersModal'
import api from '../../../api'
import { useAsync } from '../../../hooks'
import useLocalStorage from '../../../hooks/useLocalStorage'
import { PAGE_SIZE } from '../../../lib/constants'
import MonthFilter from '../../Common/MonthFilter'
import AddNewItemButton from '../../Common/AddNewItemButton'
import OfficeAuthorizations from './OfficeAuthorizations'
import useFilters from './useFilters'

const DEFAULT_FILTERS = {
  query: '',
  noFullReports: false,
  checkDate: dayjs().startOf('month').format('YYYY-MM'),
  officeLocationId: undefined,
  medicalProviderId: undefined,
  showInactive: false,
}

const useStyles = createUseStyles({
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: '10px',
    marginBottom: '10px',
    '& > form': {
      flex: '1',
      minWidth: '135px',
    },
  },
  filterContainer: {
    marginBottom: '20px',
  },
})

export default function Authorizations() {
  const { practiceId } = useParams()
  const isAdmin = useIsAdmin()
  const isPractitioner = useIsPractitioner()
  const classes = useStyles()
  const getPractice = useCallback(
    () => api.practices.getPractice(practiceId),
    [practiceId],
  )
  const { pending, value: practice, error, clearError } = useAsync(getPractice)

  const { handleFilterChange, conditions, setConditions } = useFilters({
    ...DEFAULT_FILTERS,
    practiceId,
  })
  const preAuthFilters = useMemo(
    () => ({
      q: conditions.query,
      noFullReports: conditions.noFullReports,
      checkDate: conditions.checkDate,
      officeLocationId: conditions.officeLocationId,
      medicalProviderId: conditions.medicalProviderId,
      showInactive: conditions.showInactive || undefined,
      practiceId,
    }),
    [
      conditions.checkDate,
      conditions.medicalProviderId,
      conditions.noFullReports,
      conditions.officeLocationId,
      conditions.query,
      conditions.showInactive,
      practiceId,
    ],
  )
  const { page, checkDate, perPage: urlPerPage } = conditions
  const [localStoragePageSize, setPageSize] = useLocalStorage('pageSize', PAGE_SIZE)
  const perPage = urlPerPage || localStoragePageSize

  const handlePaginationChange = useCallback(
    (newPage, newPageSize) => {
      if (newPageSize !== Number(perPage)) {
        setPageSize(newPageSize)
        setConditions({ ...conditions, page: newPage, perPage: newPageSize })
      } else {
        setConditions({ ...conditions, page: newPage })
      }
    },
    [perPage, conditions, setConditions, setPageSize],
  )

  const officeAuthorizations = useOfficeAuthorizations()
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedPatients, setSelectedPatients] = useState([])
  const hasSelected = selectedRowKeys.length > 0

  const onSelectChange = rowsIdSelection => {
    setSelectedRowKeys(rowsIdSelection)

    const preAuths = officeAuthorizations.filter(pa =>
      rowsIdSelection.includes(pa.id),
    )

    setSelectedPatients(preAuths.map(pa => pa.patient.id))
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  }

  const [remindersOptionsVisible, setRemindersOptionsVisible] = useState(false)
  const hideRemindersOptions = useCallback(
    () => setRemindersOptionsVisible(false),
    [],
  )

  const showRemindersOptions = useCallback(() => {
    setRemindersOptionsVisible(true)
  }, [])

  useFeatureGuard('in_office_checks')

  if (!pending) {
    return (
      <div>
        <PageHeader
          title="Office Checks Authorizations"
          subTitle={isAdmin && practice.name}
          hideSubTitleOnMobile
          extra={
            (isAdmin || isPractitioner) && [
              <AddNewItemButton
                key="1"
                itemName="office authorization"
                path={toPath.practices.officeAuthorizations.new({
                  practiceId,
                })}
                icon={<PlusCircleOutlined />}
              />,
            ]
          }
        />
        {/* Wraps so the month picker, the filters and the reminder button stack
            instead of overflowing once the viewport gets narrow. On a desktop
            width the three still fit on one line, so nothing moves there. */}
        <Flex gap={10} wrap>
          <MonthFilter onSubmit={handleFilterChange} currentValue={checkDate} />
          <Filters
            defaultValues={DEFAULT_FILTERS}
            currentValues={conditions}
            placeholders={{
              query: 'Search by name, device serial number or practice MRN',
            }}
            onSubmit={handleFilterChange}
            showSearch
            showMedicalProvider
            showOfficeLocation
            showInactiveCheck
          />
          <div className={cn(classes.filterContainer, classes.sendReminderButton)}>
            <Button
              icon={<PhoneOutlined />}
              disabled={!hasSelected}
              onClick={showRemindersOptions}
            >
              Send reminder
            </Button>
          </div>
        </Flex>
        <OfficeAuthorizations
          checkDate={checkDate}
          onPaginationChange={handlePaginationChange}
          page={page}
          perPage={perPage}
          filters={preAuthFilters}
          rowSelection={rowSelection}
        />
        <RemindersModal
          patientIds={selectedPatients}
          visible={remindersOptionsVisible}
          onCancel={hideRemindersOptions}
        />
      </div>
    )
  }

  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
    </div>
  )
}
